import {
    AdditiveBlending,
    CanvasTexture,
    FrontSide,
    Group,
    Line,
    LineBasicMaterial,
    LinearFilter,
    Mesh,
    MeshBasicMaterial,
    Raycaster,
    ShaderMaterial,
    Vector2,
    TextureLoader,
    Color,
} from "three";

import {
    computeBBox,
    buildMorphableCountryLines,
    buildSphere,
    buildFillSphere,
    FILL_TEX_W,
    FILL_TEX_H,
    drawCountryFill,
    findCountryAtLatLng,
} from "./utils";
import { GLOBE_RADIUS, FILL_RADIUS } from "./projections";
import GlobeMarkers from "./globeMarkers";
import GlobeHeatMap from "./globeHeatMap";

import {
    createGlobeMaterial,
    createFillSphereMaterial,
} from "./shaders/globeShader";
import {
    ZOOM_CLIP_THRESHOLD,
    CLIP_DEBOUNCE,
    COUNTRIES_FILL_COLOR,
    COUNTRIES_HOVER_COLOR,
    COUNTRIES_BORDER_COLOR,
    RESTING_BORDER_OPACITY,
    FILL_MAX_OPACITY,
    FILL_ELEVATION,
    HEATMAP_LAT_SPACING,
    HEATMAP_LNG_SPACING,
    HEATMAP_DECAY_RATE,
    HEATMAP_PAINT_STRENGTH,
} from "./config";

function lerpToward(current, target, speed, delta, threshold = 0.001) {
    const diff = target - current;
    if (Math.abs(diff) <= threshold) return target;
    return current + diff * Math.min(speed * delta, 1);
}

export default class ThreeGlobe {
    constructor() {
        this.engine = undefined;
        this.globe = undefined;
        this.atmosphereMesh = undefined;
        this.globeData = undefined;

        this._hoverFillCtx = undefined;
        this._hoverFillTexture = undefined;
        this._hoverFillMat = undefined;
        this._selectedFillCtx = undefined;
        this._selectedFillTexture = undefined;
        this._selectedFillMat = undefined;
        this._fillAnim = {
            hoverOpacity: 0,
            hoverTarget: 0,
            selectedOpacity: 0,
            selectedTarget: 0,
            fillScale: 1.0,
        };
        this._pendingHoverFill = null;
        this._pendingSelectedFill = null;

        this.sphereVertexLatLng = [];
        this.rawFeatures = new Map();
        this.geoFeatures = [];
        this.countriesData = [];

        this._atmosphereData = undefined;
        this._atmosphereMat = undefined;

        this._morphProgress = 0;
        this._morphT = 0;

        this.hoveredCountry = null;
        this._hoveredSource = null; // 'surface' | 'marker'
        this._hoveredIsClipCard = false;
        this._selectedCountry = null;
        this._showLocked = true;
        this._countryMaterials = new Map();
        this._countryGroups = new Map();
        this._countryAnimStates = new Map();

        this._hasFinePointer = window.matchMedia("(pointer: fine)").matches;

        this.raycaster = new Raycaster();
        this.mouse = new Vector2(-999, -999);
        this._pointerDownPos = null;
        this._isDragging = false;
        this._isPointerOnGlobe = false;
        this._activeTouchIds = new Set();

        this.onCountryHover = null;
        this.onCountryClick = null;
        this.onPointerMissed = null;

        this._interactionEnabled = false;
        this._activePage = "explore";
        this._clipModeActive = false;
        this._autoClipDebounce = 0;
        this._suppressClipMode = false;
        this._viewportRefreshTimer = 0;

        this._geoNameForCity = new Map();
        this._cityForGeoName = new Map();
        this.countryCodeMap = new Map();

        this.object3D = new Group();

        this.countriesGroup = new Group();
        this.pinnedMap = undefined;

        this.textureLoader = new TextureLoader();

        this.isReady = false;
        this.onReady = null;

        this._setupComplete = false;
        this._engineReady = false;
        this._voteMode = false;

        this.markers = new GlobeMarkers();
        this._heatMap = new GlobeHeatMap(
            HEATMAP_LAT_SPACING,
            HEATMAP_LNG_SPACING,
            HEATMAP_DECAY_RATE,
            HEATMAP_PAINT_STRENGTH,
        );

        this._texturePromise = this.fetchTextures();
    }

    async startSetup(dataStore) {
        const pinnedCities = JSON.parse(JSON.stringify(dataStore.pinnedCities));
        const continentOrder = JSON.parse(
            JSON.stringify(dataStore.continentOrder),
        );
        const searchableCities = JSON.parse(
            JSON.stringify(dataStore.searchableCities),
        );

        this.pinnedMap = new Map(searchableCities.map((c) => [c.name, c]));
        this.countryCodeMap = dataStore.countryCodeMap ?? new Map();
        this.markers.loadData(
            pinnedCities,
            continentOrder,
            this.engine?.isLowPower ?? false,
        );

        await this._texturePromise;
        this.setupGlobeMesh();
        await this.fetchGeodata();
        this._buildNameMaps(pinnedCities);
        this.setupFillSphere();
        this.setupAtmosphere();

        if (this.globeTextures?.length > 0) {
            this._applyOceanShader();
        }

        this.object3D.add(this.countriesGroup);
        this.object3D.add(this.markers.linesGroup);
        this.object3D.add(this.markers.markersGroup);

        this._setupComplete = true;
        this._tryStartEntrance();
    }

    setupGlobeMesh() {
        this.globeData = buildSphere(GLOBE_RADIUS, 64, 64);
        this.sphereVertexLatLng = this.globeData.vertexLatLng;

        const placeholderMat = new MeshBasicMaterial({
            color: 0x000000,
            side: FrontSide,
        });

        this.globe = new Mesh(this.globeData.geometry, placeholderMat);
        this.globe.name = "globe_base";
        this.object3D.add(this.globe);
    }

    setupFillSphere() {
        const sharedGeo = buildFillSphere(FILL_RADIUS, 64, 64).geometry;
        this._fillSphereGeometry = sharedGeo;

        const makeLayer = (renderOrder, color) => {
            const canvas = document.createElement("canvas");
            canvas.width = FILL_TEX_W;
            canvas.height = FILL_TEX_H;
            const ctx = canvas.getContext("2d");
            const texture = new CanvasTexture(canvas);
            const mat = createFillSphereMaterial(texture, color);
            const mesh = new Mesh(sharedGeo, mat);
            mesh.renderOrder = renderOrder;
            mesh.raycast = () => {};
            return { ctx, texture, mat, mesh };
        };

        const hoverLayer = makeLayer(2, COUNTRIES_HOVER_COLOR);
        this._hoverFillCtx = hoverLayer.ctx;
        this._hoverFillTexture = hoverLayer.texture;
        this._hoverFillMat = hoverLayer.mat;
        this._hoverFillMesh = hoverLayer.mesh;
        this.object3D.add(hoverLayer.mesh);

        const selectedLayer = makeLayer(3, COUNTRIES_FILL_COLOR);
        this._selectedFillCtx = selectedLayer.ctx;
        this._selectedFillTexture = selectedLayer.texture;
        this._selectedFillMat = selectedLayer.mat;
        this._selectedFillMesh = selectedLayer.mesh;
        this.object3D.add(selectedLayer.mesh);
    }

    setupAtmosphere() {
        this._atmosphereData = buildSphere(GLOBE_RADIUS * 1.15, 32, 32);

        this._atmosphereMat = new ShaderMaterial({
            vertexShader: `
                varying vec3 vWorldPos;

                void main() {
                    vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
                    gl_Position = projectionMatrix * viewMatrix * vec4(vWorldPos, 1.0);
                }
            `,
            fragmentShader: `
                uniform vec3 uColor;

                varying vec3 vWorldPos;

                void main() {
                    vec3 N = normalize(vWorldPos);
                    vec3 viewDir = normalize(cameraPosition - vWorldPos);
                    float center = max(dot(viewDir, N), 0.0);
                    float alpha = pow(center, 2.0) * .75;

                    gl_FragColor = vec4(uColor, alpha);
                }
            `,
            uniforms: {
                uColor: { value: new Color(0x83a6ce).convertLinearToSRGB() },
            },
            transparent: false,
            blending: AdditiveBlending,
            depthWrite: false,
            side: FrontSide,
        });

        this.atmosphereMesh = new Mesh(
            this._atmosphereData.geometry,
            this._atmosphereMat,
        );
        this.atmosphereMesh.renderOrder = -1;
        this.atmosphereMesh.raycast = () => {};

        this.object3D.add(this.atmosphereMesh);
    }

    async fetchGeodata() {
        try {
            const response = await fetch(
                "https://speed-cdn.expedia.com/threejs/data/world-043026.json",
            );
            const data = await response.json();

            const countriesMap = new Map();
            const features = [];
            const usStateCoords = [];

            for (const feature of data.features) {
                const code = feature.properties?.code;
                if (!code) continue;

                const isUS = code === "USA";
                let lines = [];

                if (feature.geometry.type === "Polygon") {
                    lines = buildMorphableCountryLines(
                        feature.geometry.coordinates,
                        GLOBE_RADIUS,
                    );
                    features.push({
                        code,
                        type: "Polygon",
                        coordinates: feature.geometry.coordinates,
                        bbox: computeBBox(feature.geometry.coordinates),
                    });
                    if (isUS) {
                        usStateCoords.push(feature.geometry.coordinates);
                    } else {
                        this.rawFeatures.set(code, feature.geometry);
                    }
                } else if (feature.geometry.type === "MultiPolygon") {
                    for (const poly of feature.geometry.coordinates) {
                        lines.push(
                            ...buildMorphableCountryLines(poly, GLOBE_RADIUS),
                        );
                    }
                    features.push({
                        code,
                        type: "MultiPolygon",
                        coordinates: feature.geometry.coordinates,
                        bbox: computeBBox(feature.geometry.coordinates.flat()),
                    });
                    if (isUS) {
                        usStateCoords.push(...feature.geometry.coordinates);
                    } else {
                        this.rawFeatures.set(code, feature.geometry);
                    }
                }

                if (!countriesMap.has(code)) {
                    countriesMap.set(code, { code, lines: [] });
                }
                countriesMap.get(code).lines.push(...lines);
            }

            if (usStateCoords.length > 0) {
                this.rawFeatures.set("USA", {
                    type: "MultiPolygon",
                    coordinates: usStateCoords,
                });
            }

            this.countriesData = [...countriesMap.values()];
            this.geoFeatures = features;
            this.buildCountryMeshes();
        } catch (err) {
            console.error("[ThreeGlobe] Failed to load GeoJSON:", err);
        }
    }

    async fetchTextures() {
        try {
            const colorMap = await this.textureLoader.loadAsync(
                "https://speed-cdn.expedia.com/threejs/textures/color_map.webp",
            );
            const seaMap = await this.textureLoader.loadAsync(
                "https://speed-cdn.expedia.com/threejs/textures/sea_map.webp",
            );
            const specularMap = await this.textureLoader.loadAsync(
                "https://speed-cdn.expedia.com/threejs/textures/specular_map.webp",
            );
            const bumpMap = await this.textureLoader.loadAsync(
                "https://speed-cdn.expedia.com/threejs/textures/bump_map.webp",
            );
            const normalMap = await this.textureLoader.loadAsync(
                "https://speed-cdn.expedia.com/threejs/textures/normal_map.webp",
            );

            for (const tex of [
                colorMap,
                seaMap,
                specularMap,
                bumpMap,
                normalMap,
            ]) {
                tex.minFilter = LinearFilter;
                tex.generateMipmaps = false;
            }
            this.globeTextures = [
                colorMap,
                seaMap,
                specularMap,
                bumpMap,
                normalMap,
            ];
        } catch (err) {
            console.error("[ThreeGlobe] Failed to load Textures:", err);
        }
    }

    buildCountryMeshes() {
        this._countryMaterials.clear();
        this._countryGroups.clear();
        this._countryAnimStates.clear();

        for (const country of this.countriesData) {
            const mats = [];
            const group = new Group();
            group.name = country.code;

            for (const lineData of country.lines) {
                const mat = new LineBasicMaterial({
                    color: COUNTRIES_BORDER_COLOR,
                    transparent: true,
                    opacity: RESTING_BORDER_OPACITY,
                    depthTest: true,
                    depthWrite: false,
                });

                const lineObj = new Line(lineData.geometry, mat);
                lineObj.userData = { countryCode: country.code };

                group.add(lineObj);
                mats.push(mat);
            }

            this.countriesGroup.add(group);
            this._countryMaterials.set(country.code, mats);
            this._countryGroups.set(country.code, group);
            this._countryAnimStates.set(country.code, {
                opacity: RESTING_BORDER_OPACITY,
                opacityTarget: RESTING_BORDER_OPACITY,
                scale: 1.0,
                scaleTarget: 1.0,
                materials: mats,
                group,
            });
        }
    }

    showMarkers() {
        this.markers.show();
    }

    hideMarkers() {
        this._setHovered(null);
        this.markers.setHoveredMarker(null);
        this.markers.exitClipMode();
        this.markers.hide();
    }

    setShowLocked(show) {
        this._showLocked = show;
        this.markers.setShowLocked(show);
    }

    setSelectedCountry(name, activePage) {
        const prev = this._selectedCountry;

        this._setHovered(null);
        this.markers.setHoveredMarker(null);

        if (prev && prev !== name) {
            this._setBorderOpacity(prev, RESTING_BORDER_OPACITY);
            this._setCountryElevation(prev, false);
        }

        this._selectedCountry = name;
        this.markers.setActiveMarker(name ?? null);
        this.markers.setActiveClipCard(name ?? null);

        if (name) {
            this._setBorderOpacity(name, 1);
            this._setCountryElevation(name, true);

            const geo = this.rawFeatures.get(this._toGeoName(name));
            if (geo && this._selectedFillCtx) {
                const pinnedInfo = this.pinnedMap.get(name);
                const isLocked = pinnedInfo?.locked ?? false;

                if (this._fillAnim.selectedOpacity < 0.05) {
                    drawCountryFill(this._selectedFillCtx, geo, isLocked);

                    this._selectedFillTexture.needsUpdate = true;
                    this._fillAnim.selectedTarget = FILL_MAX_OPACITY;
                    this._pendingSelectedFill = null;
                } else {
                    this._pendingSelectedFill = { geo, isLocked };
                    this._fillAnim.selectedTarget = 0;
                }
            }
        } else {
            if (prev) this._setCountryElevation(prev, false);
            this._pendingSelectedFill = null;
            this._fillAnim.selectedTarget = 0;
        }

        if (activePage === "explore") {
            if (name) {
                this.markers.focusOn();
            } else {
                this.markers.exitClipMode();
                this.markers.clearFocus();
                this._clipModeActive = false;
                this._autoClipDebounce = 0;
                this._suppressClipMode = true;
            }
        }
    }

    setInteractionEnabled(enabled) {
        this._interactionEnabled = enabled;
        if (!enabled) {
            this._globeShaderMaterial?.uniforms.uMouseUV.value.set(-1, -1);
            this._setHovered(null);
            this.markers.setHoveredClipCard(null);
        }
    }

    setVoteMode(enabled) {
        this._voteMode = enabled;
    }

    setActivePage(page) {
        this._activePage = page;
        this._suppressClipMode = false;
        if (page !== "explore" && this._clipModeActive) {
            this.markers.exitClipMode();
            this.markers.clearFocus();
            this._clipModeActive = false;
        }
    }

    setShowMarkers(show) {
        this.markers.setShowMarkers(show);
    }

    setShowLines(show) {
        this.markers.setShowLines(show);
    }

    get morphT() {
        return this._morphT;
    }

    get sphereMesh() {
        return this.globe;
    }

    _updateCursor() {
        if (!this._canvas) return;

        const canDrag = this._interactionEnabled;
        const isClickable =
            this.hoveredCountry &&
            (this._voteMode || !!this.pinnedMap?.get(this.hoveredCountry));

        if (this._isDragging && canDrag) {
            this._canvas.style.cursor = "grabbing";
        } else if (isClickable) {
            this._canvas.style.cursor = "pointer";
        } else if (this._isPointerOnGlobe && canDrag) {
            this._canvas.style.cursor = "grab";
        } else {
            this._canvas.style.cursor = "default";
        }
    }

    _onPointerMove = (event) => {
        if (!this._canvas || !this._canvasRect) return;
        if (!this._hasFinePointer) return;
        const rect = this._canvasRect;
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    };

    _onPointerDown = (event) => {
        if (event.pointerType === "touch") {
            if (this._activeTouchIds.size >= 1) {
                // Second finger down = pinch gesture → deselect
                this.onPointerMissed?.();
                this._pointerDownPos = null;
            }
            this._activeTouchIds.add(event.pointerId);
        }
        this._pointerDownPos = { x: event.clientX, y: event.clientY };
        this._isDragging = true;
        this._updateCursor();
    };

    _onPointerUp = (event) => {
        this._isDragging = false;

        if (event.pointerType === "touch") {
            this._activeTouchIds.delete(event.pointerId);
        }

        if (!this._pointerDownPos) {
            this._updateCursor();
            return;
        }

        const dx = event.clientX - this._pointerDownPos.x;
        const dy = event.clientY - this._pointerDownPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        this._pointerDownPos = null;

        if (dist > 15) {
            if (event.pointerType === "touch") this.mouse.set(-999, -999);
            return;
        }

        if (event.pointerType === "touch") {
            const rect = this._canvasRect;
            if (!rect) return;

            this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            this._updateRaycast();

            this.mouse.set(-999, -999);
        }

        if (
            this.hoveredCountry &&
            this.hoveredCountry !== this._selectedCountry
        ) {
            if (this._voteMode) {
                const pinnedInfo = this.pinnedMap.get(this.hoveredCountry);
                const geoCode = this._toGeoName(this.hoveredCountry);
                const countryInfo = this.countryCodeMap.get(geoCode);
                const clickName =
                    pinnedInfo?.countryName ??
                    countryInfo?.name ??
                    this.hoveredCountry;
                this.onCountryClick?.(
                    clickName,
                    pinnedInfo?.locked ?? countryInfo?.locked ?? false,
                    pinnedInfo?.continent ?? countryInfo?.continent ?? null,
                );
            } else {
                const pinnedInfo = this.pinnedMap.get(this.hoveredCountry);
                if (pinnedInfo && this.onCountryClick) {
                    this.onCountryClick(
                        this.hoveredCountry,
                        !!pinnedInfo.locked,
                        pinnedInfo.continent,
                    );
                }
            }
        } else {
            this.onPointerMissed?.();
        }

        this._updateCursor();
    };

    _onPointerCancel = (event) => {
        this._isDragging = false;
        this._pointerDownPos = null;
        if (event.pointerType === "touch") {
            this._activeTouchIds.delete(event.pointerId);
        }
        this._updateCursor();
    };

    _refreshCanvasRect = () => {
        if (this._canvas) {
            this._canvasRect = this._canvas.getBoundingClientRect();
        }
    };

    onAdd(engine) {
        this.engine = engine;
        this._canvas = engine.DOM.canvas;
        this._canvasRect = this._canvas?.getBoundingClientRect() ?? null;

        if (this._canvas) {
            this._canvas.addEventListener("pointermove", this._onPointerMove);
            this._canvas.addEventListener("pointerdown", this._onPointerDown);
            this._canvas.addEventListener("pointerup", this._onPointerUp);
            this._canvas.addEventListener(
                "pointercancel",
                this._onPointerCancel,
            );

            this._canvasParent = this._canvas.parentElement;
            this._canvasParent?.addEventListener(
                "transitionend",
                this._refreshCanvasRect,
            );
        }

        this._engineReady = true;
        this._tryStartEntrance();
    }

    resize = () => {
        this._refreshCanvasRect();
    };

    update(delta, elapsed) {
        if (!this.object3D || !this.engine?.camera) return;

        const d = Math.min(delta, 0.05);

        if (this._globeShaderMaterial) {
            this._globeShaderMaterial.uniforms.uTime.value =
                (elapsed ?? 0) % 10000.0;
        }

        this._updateRaycast();
        this._updateCountryAnimations(d);
        this._updateFillAnimations(d);

        if (this._heatMap && this._globeShaderMaterial) {
            const mouseUV = this._globeShaderMaterial.uniforms.uMouseUV.value;
            this._heatMap.update(mouseUV.x >= 0 ? mouseUV : null);
        }

        if (this._activePage === "explore" && this._interactionEnabled) {
            this._stepAutoClip(d);
        }

        this.markers.update(this._morphT, this.engine.camera, d);
    }

    dispose() {
        if (this.globe) {
            this.globe.geometry.dispose();
            this.globe.material.dispose();
            this.object3D.remove(this.globe);
        }

        if (this.globeTextures) {
            for (const tex of this.globeTextures) tex.dispose();
            this.globeTextures = [];
        }

        for (const state of this._countryAnimStates.values()) {
            state.materials.forEach((m) => m.dispose());
            state.group.children.forEach((line) => line.geometry?.dispose());
        }
        this.object3D.remove(this.countriesGroup);

        this._hoverFillMat?.dispose();
        this._hoverFillTexture?.dispose();
        if (this._hoverFillMesh) this.object3D.remove(this._hoverFillMesh);

        this._selectedFillMat?.dispose();
        this._selectedFillTexture?.dispose();
        if (this._selectedFillMesh)
            this.object3D.remove(this._selectedFillMesh);

        this._fillSphereGeometry?.dispose();

        if (this._atmosphereData) this._atmosphereData.geometry.dispose();
        if (this._atmosphereMat) this._atmosphereMat.dispose();
        if (this.atmosphereMesh) this.object3D.remove(this.atmosphereMesh);

        if (this._heatMap) this._heatMap.dispose();

        this.markers.dispose();
        this.object3D.remove(this.markers.linesGroup);
        this.object3D.remove(this.markers.markersGroup);

        if (this._canvas) {
            this._canvas.removeEventListener(
                "pointermove",
                this._onPointerMove,
            );
            this._canvas.removeEventListener(
                "pointerdown",
                this._onPointerDown,
            );
            this._canvas.removeEventListener("pointerup", this._onPointerUp);
            this._canvas.removeEventListener(
                "pointercancel",
                this._onPointerCancel,
            );
        }

        this._canvasParent?.removeEventListener(
            "transitionend",
            this._refreshCanvasRect,
        );
        this._canvasParent = null;
    }

    _stepAutoClip(delta) {
        const camDist = this.engine.camera.position.length();

        if (this._suppressClipMode) {
            if (camDist > ZOOM_CLIP_THRESHOLD) this._suppressClipMode = false;
            else return;
        }

        const shouldBeActive = camDist <= ZOOM_CLIP_THRESHOLD;

        if (shouldBeActive && this._clipModeActive) {
            this._viewportRefreshTimer += delta;
            if (this._viewportRefreshTimer >= 0.1) {
                this._viewportRefreshTimer = 0;
                this.markers.enterClipMode(this.engine.camera, this._morphT);
            }
        } else if (shouldBeActive !== this._clipModeActive) {
            this._autoClipDebounce += delta;
            if (this._autoClipDebounce >= CLIP_DEBOUNCE) {
                this._autoClipDebounce = 0;
                if (shouldBeActive) {
                    this._clipModeActive = true;
                    this._viewportRefreshTimer = 0;
                    this.markers.enterClipMode(
                        this.engine.camera,
                        this._morphT,
                    );
                } else {
                    this._clipModeActive = false;
                    this.markers.exitClipMode();
                    this.markers.clearFocus();
                }
            }
        } else {
            this._autoClipDebounce = 0;
        }
    }

    _tryStartEntrance() {
        if (!this._setupComplete || !this._engineReady || this.isReady) return;
        this.isReady = true;
        this.onReady?.();
    }

    _applyOceanShader() {
        const material = createGlobeMaterial(
            this.globeTextures[0],
            this.globeTextures[1],
            this.globeTextures[2],
            this.globeTextures[3],
            this.globeTextures[4],
            this._heatMap.texture,
            HEATMAP_LNG_SPACING,
            this.engine.isLowPower ? 3 : 4,
            this.engine.isLowPower ? 1 : 4,
            this.engine.isLowPower ? 1.9 : 1.1,
        );

        if (this.globe.material) {
            this.globe.material.dispose();
        }

        this.globe.material = material;
        this._globeShaderMaterial = material;
    }

    _buildNameMaps(pinnedCities) {
        for (const city of pinnedCities) {
            const geoName = findCountryAtLatLng(
                city.lat,
                city.lng,
                this.geoFeatures,
            );
            if (!geoName) continue;
            this._geoNameForCity.set(city.name, geoName);
            if (!this._cityForGeoName.has(geoName)) {
                this._cityForGeoName.set(geoName, city.name);
            }
        }
    }

    _toGeoName(name) {
        const fromCity = this._geoNameForCity.get(name);

        if (fromCity) return fromCity;
        if (this._countryAnimStates.has(name)) return name;

        for (const [code, info] of this.countryCodeMap) {
            if (info.name === name) return code;
        }

        return name;
    }

    _toCityName(name) {
        return this._cityForGeoName.get(name) ?? name;
    }

    _isSelectedCountry(name) {
        if (!this._selectedCountry || !name) return false;
        if (name === this._selectedCountry) return true;
        return this._toGeoName(name) === this._toGeoName(this._selectedCountry);
    }

    _updateRaycast() {
        if (!this.globe || !this.geoFeatures.length) return;

        this.raycaster.setFromCamera(this.mouse, this.engine.camera);

        const globeIntersects = this.raycaster.intersectObject(this.globe);
        if (this._globeShaderMaterial) {
            if (globeIntersects.length > 0 && globeIntersects[0].uv) {
                this._globeShaderMaterial.uniforms.uMouseUV.value.copy(
                    globeIntersects[0].uv,
                );
            } else {
                this._globeShaderMaterial.uniforms.uMouseUV.value.set(-1, -1);
            }
        }

        this._isPointerOnGlobe = globeIntersects.length > 0;

        if (!this._interactionEnabled) return;

        this._hoveredIsClipCard = false;

        const markerMeshes = this.markers.getMarkerMeshes();
        const markerHits = this.raycaster.intersectObjects(markerMeshes);
        if (markerHits.length > 0) {
            const countryName = markerHits[0].object.userData.countryName;
            if (countryName === this._selectedCountry) {
                this.markers.setHoveredMarker(null);
                this._globeShaderMaterial?.uniforms.uMouseUV.value.set(-1, -1);
                return;
            }
            this.markers.setHoveredMarker(countryName);
            if (
                this.hoveredCountry !== countryName ||
                this._hoveredSource !== "marker"
            ) {
                this._setHovered(countryName, "marker");
            }
            this._globeShaderMaterial?.uniforms.uMouseUV.value.set(-1, -1);
            return;
        }

        this.markers.setHoveredMarker(null);

        const clipCardMeshes = this.markers.getClipCardMeshes();
        if (clipCardMeshes.length > 0) {
            const clipHits = this.raycaster.intersectObjects(clipCardMeshes);
            if (clipHits.length > 0) {
                const countryName = clipHits[0].object.userData.countryName;
                if (countryName === this._selectedCountry) {
                    this.markers.setHoveredClipCard(null);
                    this._globeShaderMaterial?.uniforms.uMouseUV.value.set(
                        -1,
                        -1,
                    );
                    return;
                }
                this.markers.setHoveredClipCard(countryName);
                if (
                    this.hoveredCountry !== countryName ||
                    this._hoveredSource !== "marker"
                ) {
                    this._setHovered(countryName, "marker");
                }
                this._hoveredIsClipCard = true;
                this._globeShaderMaterial?.uniforms.uMouseUV.value.set(-1, -1);
                return;
            }
            this.markers.setHoveredClipCard(null);
        }

        let countryUnderMouse = null;

        if (globeIntersects.length > 0) {
            const point = globeIntersects[0].point;
            const r = point.length();
            if (r > 0) {
                const lat = Math.asin(point.y / r) * (180 / Math.PI);
                let lng = Math.atan2(point.z, -point.x) * (180 / Math.PI) - 180;
                if (lng < -180) lng += 360;

                countryUnderMouse = findCountryAtLatLng(
                    lat,
                    lng,
                    this.geoFeatures,
                );
            }
        }

        if (
            this.hoveredCountry === this._toCityName(countryUnderMouse) &&
            this._hoveredSource === "surface"
        )
            return;

        this._setHovered(countryUnderMouse, "surface");
    }

    _setHovered(rawName, source = "surface") {
        const geoCode =
            source === "surface" ? rawName : this._toGeoName(rawName);
        const name = rawName ? this._toCityName(rawName) : null;
        const prev = this.hoveredCountry;

        if (prev === name && this._hoveredSource === source) return;

        if (prev && prev !== this._selectedCountry) {
            this._setBorderOpacity(prev, RESTING_BORDER_OPACITY);
        }

        this._hoveredSource = source;

        const alreadySelected = this._isSelectedCountry(name);

        if (name) {
            const countryInfo = this.countryCodeMap.get(geoCode);
            const pinnedInfo = this.pinnedMap.get(name);
            const isLocked = pinnedInfo?.locked ?? countryInfo?.locked ?? false;

            if (!this._voteMode && isLocked && !this._showLocked) {
                this.hoveredCountry = null;
                this.onCountryHover?.(null);
                this._updateCursor();
                return;
            }

            if (!alreadySelected) {
                this._setBorderOpacity(name, 1);

                const geo = this.rawFeatures.get(geoCode);
                if (geo && this._hoverFillCtx) {
                    if (this._fillAnim.hoverOpacity < 0.05) {
                        drawCountryFill(this._hoverFillCtx, geo, isLocked);
                        this._hoverFillTexture.needsUpdate = true;
                        this._fillAnim.hoverTarget = FILL_MAX_OPACITY;
                        this._pendingHoverFill = null;
                    } else {
                        this._pendingHoverFill = { geo, isLocked };
                        this._fillAnim.hoverTarget = 0;
                    }
                }
            }

            this.hoveredCountry = name;

            const displayName =
                source === "marker"
                    ? (pinnedInfo?.countryName ?? countryInfo?.name ?? name)
                    : (countryInfo?.name ?? name);

            this.onCountryHover?.(
                displayName,
                isLocked,
                countryInfo?.continent ?? pinnedInfo?.continent ?? null,
            );
            this._updateCursor();
        } else {
            this.hoveredCountry = null;
            this._hoveredSource = null;
            this._fillAnim.hoverTarget = 0;
            this.onCountryHover?.(null);
            this._updateCursor();
        }
    }

    _setBorderOpacity(countryName, opacity) {
        const state = this._countryAnimStates.get(this._toGeoName(countryName));
        if (state) state.opacityTarget = opacity;
    }

    _setCountryElevation(countryName, elevated) {
        const state = this._countryAnimStates.get(this._toGeoName(countryName));
        if (state) state.scaleTarget = elevated ? FILL_ELEVATION : 1.0;
    }

    _updateCountryAnimations(delta) {
        const OPACITY_SPEED = 10;
        const SCALE_SPEED = 6;

        for (const state of this._countryAnimStates.values()) {
            const newOpacity = lerpToward(
                state.opacity,
                state.opacityTarget,
                OPACITY_SPEED,
                delta,
            );
            if (newOpacity !== state.opacity) {
                state.opacity = newOpacity;
                state.materials.forEach((m) => (m.opacity = newOpacity));
            }

            const newScale = lerpToward(
                state.scale,
                state.scaleTarget,
                SCALE_SPEED,
                delta,
                0.0001,
            );
            if (newScale !== state.scale) {
                state.scale = newScale;
                state.group.scale.setScalar(newScale);
            }
        }
    }

    _updateFillAnimations(delta) {
        const FILL_SPEED = 24;
        const FILL_SCALE_SPEED = 6;

        const fa = this._fillAnim;

        const newHoverOpacity = lerpToward(
            fa.hoverOpacity,
            fa.hoverTarget,
            FILL_SPEED,
            delta,
        );
        if (newHoverOpacity !== fa.hoverOpacity) {
            fa.hoverOpacity = newHoverOpacity;
            if (this._hoverFillMat)
                this._hoverFillMat.uniforms.uOpacity.value = newHoverOpacity;
        }

        if (this._pendingHoverFill && fa.hoverOpacity < 0.05) {
            const { geo, isLocked } = this._pendingHoverFill;
            this._pendingHoverFill = null;
            drawCountryFill(this._hoverFillCtx, geo, isLocked);
            this._hoverFillTexture.needsUpdate = true;
            fa.hoverTarget = FILL_MAX_OPACITY;
        }

        if (this._pendingSelectedFill && fa.selectedOpacity < 0.05) {
            const { geo, isLocked } = this._pendingSelectedFill;
            this._pendingSelectedFill = null;
            drawCountryFill(this._selectedFillCtx, geo, isLocked);
            this._selectedFillTexture.needsUpdate = true;
            fa.selectedTarget = FILL_MAX_OPACITY;
        }

        const newSelectedOpacity = lerpToward(
            fa.selectedOpacity,
            fa.selectedTarget,
            FILL_SPEED,
            delta,
        );
        if (newSelectedOpacity !== fa.selectedOpacity) {
            fa.selectedOpacity = newSelectedOpacity;
            if (this._selectedFillMat)
                this._selectedFillMat.uniforms.uOpacity.value =
                    newSelectedOpacity;
        }

        const fillScaleTarget = fa.selectedTarget > 0 ? FILL_ELEVATION : 1.0;
        const newFillScale = lerpToward(
            fa.fillScale,
            fillScaleTarget,
            FILL_SCALE_SPEED,
            delta,
            0.0001,
        );
        if (newFillScale !== fa.fillScale) {
            fa.fillScale = newFillScale;
            if (this._selectedFillMesh)
                this._selectedFillMesh.scale.setScalar(newFillScale);
        }
    }
}
